import React, { useEffect, useMemo, useState, useCallback } from "react"
import { useDebounce } from "hooks/useDebounce"
import { withRouter } from "react-router-dom"
import TableContainer from "components/Common/TableContainer"
import { Card, CardBody, Col, Container, Row } from "reactstrap"
import * as url from "../../helpers/url_helper"

import { Label, BrandName, UserCashback, State, MerchantName, MerchantLocation } from "./offerCol"

import Breadcrumbs from "components/Common/Breadcrumb"

import {
    getApproverOffers as onGetApproverOffers,
    clearApproverOffers,
    getOfferStateHistory as onGetOfferStateHistory,
    clearOfferStateHistory,
    publishOffer,
    denyOffer
} from "store/offers/actions"

import { useSelector, useDispatch } from "react-redux"
import AccessControlWrapper from "components/Common/PermissionButton"
import { PageTitle } from "constants/ui"
import CButton from "components/Common/CButton"
import IconButton from "components/Common/IconButton"
import OfferStateHistoryModal from "components/Offers/OfferStateHistoryModel"
import OfferActionModal from "components/Offers/OfferActionModal"
import { getFirstTabPermission } from "helpers/tab_helper"
import { calculateStartIndex } from "helpers/pagination_helper"

const ApproverOffersList = props => {
    document.title = PageTitle.APPROVER_OFFERS_LIST

    const [search, setSearch] = useState("")
    const [page, setPage] = useState(1)
    const [historyModal, setHistoryModal] = useState(false)
    const [actionModal, setActionModal] = useState({ isOpen: false, offerID: null, actionType: null })

    const dispatch = useDispatch()

    const { history } = props

    const { data, userMenu } = useSelector(state => ({
        data: state.offers,
        userMenu: state.userMenu?.userMenu || [],
    }))

    const firstTabPermission = getFirstTabPermission("APPROVER_OFFERS_DETAIL", userMenu)

    const columns = useMemo(
        () => [
            {
                Header: "Image",
                accessor: "image",
                disableFilters: true,
                filterable: true,

                accessor: cellProps => (
                    <>
                        {!cellProps.image ? (
                            <div className="avatar-xs">
                                <span className="avatar-title rounded-circle">
                                    {cellProps.label?.charAt(0) || "O"}
                                </span>
                            </div>
                        ) : (
                            <div>
                                <img
                                    className="rounded-circle avatar-xs"
                                    src={cellProps.image}
                                    alt=""
                                />
                            </div>
                        )}
                    </>
                ),
            },
            {
                Header: "Label",
                accessor: "label",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <Label {...cellProps} />
                },
            },
            {
                Header: "Brand",
                accessor: "brand",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <BrandName {...cellProps} />
                },
            },
            {
                Header: "Merchant Name",
                accessor: "merchant.displayName",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <MerchantName {...cellProps} />
                },
            },
            {
                Header: "Merchant Location",
                accessor: "merchant.legalEntityAddress",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <MerchantLocation {...cellProps} />
                },
            },
            {
                Header: "Cashback %",
                accessor: "userCashbackPercentage",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <UserCashback {...cellProps} />
                },
            },
            {
                Header: "State",
                accessor: "state",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <State {...cellProps} />
                },
            },
            {
                Header: "Action",
                Cell: cellProps => {
                    return (
                        <div className="d-flex gap-2">
                            <AccessControlWrapper
                                actionType={firstTabPermission?.actionType || "get"}
                                contextUri={firstTabPermission?.contextUri || url.GET_APPROVER_OFFERS}
                                history={history}
                            >
                                <IconButton.View
                                    onClick={() => {
                                        const offerID = cellProps.row.original.offerID
                                        history.push(`/approver-offers/${offerID}`)
                                    }}
                                />
                            </AccessControlWrapper>
                            {/* <AccessControlWrapper
                                actionType="post"
                                contextUri={url.MANAGE_CURATOR_OFFER}
                                history={history}
                            >
                                <IconButton
                                    icon="bx bx-copy"
                                    color="secondary"
                                    title="Copy Offer"
                                    onClick={() => {
                                        const offerID = cellProps.row.original.offerID
                                        history.push(`/curator-offers/add?copyFrom=${offerID}&fromType=approver`)
                                    }}
                                />
                            </AccessControlWrapper> */}
                            <AccessControlWrapper
                                actionType="get"
                                contextUri={url.GET_OFFER_STATE_HISTORY}
                                history={history}
                            >
                                <IconButton.History onClick={() => showHistoryModal(cellProps.row.original.offerID)} />
                            </AccessControlWrapper>
                            {cellProps.row.original.state.toLowerCase() === 'processed' && (
                                <>
                                    <AccessControlWrapper
                                        actionType="post"
                                        contextUri={url.PUBLISH_OFFER}
                                        history={history}
                                    >
                                        <IconButton.Publish
                                            onClick={() => handleActionClick(cellProps.row.original.offerID, 'publish')}
                                        />
                                    </AccessControlWrapper>
                                    <AccessControlWrapper
                                        actionType="post"
                                        contextUri={url.DENY_OFFER}
                                        history={history}
                                    >
                                        <IconButton.Deny
                                            onClick={() => handleActionClick(cellProps.row.original.offerID, 'deny')}
                                        />
                                    </AccessControlWrapper>
                                </>
                            )}
                        </div>
                    )
                },
            },
        ],
        []
    )

    useEffect(() => {
        dispatch(
            onGetApproverOffers({
                headers: {
                    "input-parameters": {},
                },
                pagination: { startIndex: 0 },
            })
        )
    }, [dispatch])

    useEffect(() => {
        return () => {
            dispatch(clearApproverOffers())
            dispatch(clearOfferStateHistory())
        }
    }, [dispatch])

    const rowClick = (e, row) => {
        const {
            original: { offerID },
        } = row
        history.push({
            pathname: `/approver-offers/${offerID}`,
        })
    }

    const showHistoryModal = (offerID) => {
        setHistoryModal(true)
        dispatch(
            onGetOfferStateHistory({
                headers: {
                    "input-parameters": { offerID: offerID },
                },
            })
        )
    }

    const closeHistoryModal = () => {
        setHistoryModal(false)
        dispatch(clearOfferStateHistory())
    }

    const dispatchSearch = useCallback((value) => {
        setPage(1)
        dispatch(
            onGetApproverOffers({
                headers: {
                    "input-parameters": { keyword: value || "" },
                },
                pagination: { startIndex: 0 },
            })
        )
    }, [dispatch])

    const [debouncedSearch] = useDebounce(dispatchSearch)

    const handleSearchChange = e => {
        const { value } = e.target
        setSearch(value)
        debouncedSearch(value)
    }

    const handlePageChange = page => {
        const startIndex = (page - 1) * 10
        setPage(page)
        dispatch(
            onGetApproverOffers({
                headers: {
                    "input-parameters": { keyword: search || "" },
                },
                pagination: { startIndex },
            })
        )
    }

    const handleRefresh = () => {
        dispatch(
            onGetApproverOffers({
                headers: {
                    "input-parameters": { keyword: search || "" },
                },
                pagination: { startIndex: (page - 1) * 10 },
            })
        )
    }

    const handleActionClick = (offerID, actionType) => {
        setActionModal({
            isOpen: true,
            offerID: offerID,
            actionType: actionType
        })
    }

    const closeActionModal = () => {
        setActionModal({ isOpen: false, offerID: null, actionType: null })
    }

    const handleActionSubmit = (payload) => {
        const payloadWithPagination = {
            ...payload,
            refreshPagination: {
                startIndex: calculateStartIndex(page),
                inputParams: { keyword: search || "" }
            }
        }
        if (actionModal.actionType === 'publish') {
            dispatch(publishOffer(payloadWithPagination))
        } else if (actionModal.actionType === 'deny') {
            dispatch(denyOffer(payloadWithPagination))
        }
    }

    const getActionModalTitle = () => {
        if (actionModal.actionType === 'publish') return 'Publish Offer'
        if (actionModal.actionType === 'deny') return 'Deny Offer'
        return 'Action'
    }

    const getActionModalUri = () => {
        if (actionModal.actionType === 'publish') return url.PUBLISH_OFFER
        if (actionModal.actionType === 'deny') return url.DENY_OFFER
        return ''
    }

    const getActionConfirmMessage = () => {
        if (actionModal.actionType === 'publish') return 'Are you sure you want to publish this offer?'
        if (actionModal.actionType === 'deny') return 'Are you sure you want to deny this offer?'
        return 'Are you sure you want to proceed?'
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs
                        title={PageTitle.APPROVER_OFFERS_LIST}
                        titleLink="/approver-offers"
                        breadcrumbItem={PageTitle.APPROVER_OFFERS_LIST}
                    />
                    <Row>
                        <Col lg="12">
                            <Card>
                                <CardBody>
                                    <Row>
                                        <Col md="6">
                                            <div>
                                                <input
                                                    type="text"
                                                    placeholder="search approver offers"
                                                    onChange={handleSearchChange}
                                                    className="form-control w-50 rounded-pill"
                                                />
                                            </div>
                                        </Col>
                                        <Col md="6">
                                            <div className="text-sm-end">
                                                {/* Future: Add Offer button can be added here */}
                                            </div>
                                        </Col>
                                    </Row>

                                    <TableContainer
                                        columns={columns}
                                        data={data?.approverError?.message ? [] : data.approverOffers}
                                        isGlobalFilter={false}
                                        isAddUserList={true}
                                        rowClick={rowClick}
                                        protectedRowClick={true}
                                        contextUri={url.GET_APPROVER_OFFERS}
                                        actionType="get"
                                        customPageSize={10}
                                        className="custom-header-css"
                                        columnFilter={false}
                                        changePageSize={false}
                                        onPageChange={handlePageChange}
                                        currentPage={page}
                                        hasNextPage={!data?.approverError?.message && data.approverOffers?.length >= 10}
                                        onRefresh={handleRefresh}
                                        refreshContextUri={url.GET_APPROVER_OFFERS}
                                        refreshActionType="get"
                                        history={history}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* History Modal */}
            <OfferStateHistoryModal
                isOpen={historyModal}
                toggle={closeHistoryModal}
                historyData={data.offerStateHistory}
                isLoading={data.isLoadingOfferStateHistory}
            />

            {/* Action Modal */}
            <OfferActionModal
                isOpen={actionModal.isOpen}
                toggle={closeActionModal}
                title={getActionModalTitle()}
                offerID={actionModal.offerID}
                onSubmit={handleActionSubmit}
                isLoading={data.isLoadingPublishOffer || data.isLoadingDenyOffer}
                contextUri={getActionModalUri()}
                confirmMessage={getActionConfirmMessage()}
            />
        </React.Fragment>
    )
}

export default withRouter(ApproverOffersList)